import { compactObject } from './_internal';
import isPresent from './is_present';

const DEFAULT_POSTHOG_HOST = 'https://eu.i.posthog.com';
const CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];
let fetchedPosthogConfig = null;
const FIRST_TOUCH_STORAGE_KEY = 'seedlang-posthog-first-touch';
let landingCampaignProperties = {};
let firstTouchCampaignProperties = {};

function analyticsMembershipTypeId(user, site) {
  if (!site || site.isSeedlang) {
    return user && user.membershipTypeId;
  }

  if (typeof user?.userGroupForSite !== 'function') {
    return null;
  }

  return user?.userGroupForSite(site)?.membershipTypeId;
}

function posthogPersonProperties(user, site) {
  const promotion = user && user.signedUpWithPromotion;

  return compactObject({
    email: user && user.email,
    name: user && user.name,
    language_id: user && user.languageId,
    target_language_id: user && user.targetLanguageId,
    level_id: user && user.levelId,
    level_abbreviation: user && user.levelAbbreviation,
    membership_type_id: analyticsMembershipTypeId(user, site),
    language_signed_up_with: user && user.languageSignedUpWith,
    pro: Boolean(user && user.membershipTypeId),
    experiment: user && user.experiment,
    is_member: user && user.isMember,
    permanent_membership: user && user.permanentMembership,
    guest: user && user.guest,
    converted_from_guest: user && user.convertedFromGuest,
    promotion_slug: promotion && promotion.slug,
    learning_preference: user && user.learningPreference,
    country_code: user && user.countryCode,
    site_id: site && site.id,
    site_name: (site && site.teamName) || (site && site.name),
  });
}

function browserSupportsPosthog() {
  return typeof window !== 'undefined'
    && typeof document !== 'undefined'
    && typeof globalThis !== 'undefined'
    && typeof Promise !== 'undefined'
    && typeof Promise.prototype.finally === 'function';
}

function browserSupportsPosthogPerformanceCapture() {
  return typeof PerformanceObserver !== 'undefined'
    && Array.prototype
    && typeof Array.prototype.at === 'function';
}

function isDebugLoggingHost() {
  if (!browserSupportsPosthog()) {
    return false;
  }

  const hostname = window.location && window.location.hostname;
  return Boolean(
    hostname && (
      ['localhost', '127.0.0.1'].includes(hostname)
      || hostname.endsWith('.ngrok.io')
    )
  );
}

function metaContent(name) {
  const metaTag = typeof document.querySelector === 'function' ? document.querySelector(`meta[name="${name}"]`) : null;
  return metaTag ? metaTag.getAttribute('content') : null;
}

function posthogHostToAssetHost(host) {
  return host.includes('.i.posthog.com') ? host.replace('.i.posthog.com', '-assets.i.posthog.com') : host;
}

function initializedPosthog() {
  if (!browserSupportsPosthog() || !window.__seedlangPosthogInitialized) {
    return null;
  }

  return window.posthog || null;
}

function installSnippet() {
  if (!browserSupportsPosthog()) {
    return null;
  }

  const existingPosthog = window.posthog;
  if (existingPosthog && existingPosthog.__SV) {
    return existingPosthog;
  }

  const posthog = existingPosthog || [];
  posthog.init = function init(apiKey, config, name) {
    function makeStub(target, methodName) {
      const parts = methodName.split('.');
      let stubTarget = target;
      let stubMethod = methodName;

      if (parts.length === 2) {
        stubTarget = target[parts[0]];
        stubMethod = parts[1];
      }

      stubTarget[stubMethod] = function posthogStub() {
        stubTarget.push([stubMethod].concat(Array.prototype.slice.call(arguments, 0)));
      };
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.src = posthogHostToAssetHost(config.api_host || DEFAULT_POSTHOG_HOST) + '/static/array.js';

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    let instance = posthog;
    let instanceName = name;
    if (typeof instanceName !== 'undefined') {
      instance = posthog[instanceName] = [];
    } else {
      instanceName = 'posthog';
    }

    instance.people = instance.people || [];
    instance.toString = function toString(stub) {
      let str = 'posthog';
      if (instanceName !== 'posthog') {
        str += `.${instanceName}`;
      }
      if (!stub) {
        str += ' (stub)';
      }
      return str;
    };
    instance.people.toString = function peopleToString() {
      return instance.toString(1) + '.people (stub)';
    };
    const methods = 'init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug'.split(' ');
    for (let i = 0; i < methods.length; i += 1) {
      makeStub(instance, methods[i]);
    }
    posthog._i.push([apiKey, config, instanceName]);
  };

  window.posthog = posthog;
  posthog._i = posthog._i || [];
  posthog.__SV = 1;
  return posthog;
}

function getPosthogConfig() {
  if (!browserSupportsPosthog()) {
    return {};
  }

  if (fetchedPosthogConfig) {
    return fetchedPosthogConfig;
  }

  return {
    apiKey: metaContent('seedlang-posthog-api-key') || process.env.REACT_APP_POSTHOG_API_KEY,
    host: metaContent('seedlang-posthog-host') || process.env.REACT_APP_POSTHOG_HOST || DEFAULT_POSTHOG_HOST,
  };
}

// PostHog may only initialize after cookie consent or after client-side routing
// has dropped the query string, so the landing URL's campaign params are kept in
// memory until then and only persisted by PostHog once tracking is allowed.
export function captureLandingCampaignParams() {
  if (!browserSupportsPosthog() || typeof URLSearchParams === 'undefined') {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  landingCampaignProperties = CAMPAIGN_PARAMS.reduce((result, param) => {
    const value = searchParams.get(param);
    if (isPresent(value)) {
      result[`first_touch_${param}`] = value;
    }
    return result;
  }, {});
}

export async function bootstrapPosthogConfig() {
  if (!browserSupportsPosthog()) {
    return null;
  }

  const existingConfig = getPosthogConfig();
  if (existingConfig.apiKey) {
    return existingConfig;
  }

  try {
    const response = await fetch('/api/posthog_config');
    if (!response.ok) {
      return null;
    }

    const json = await response.json();
    const apiKey = json && (json.apiKey || json.api_key);
    if (!apiKey) {
      return null;
    }

    fetchedPosthogConfig = {
      apiKey: apiKey,
      host: json.host || DEFAULT_POSTHOG_HOST,
    };
    return fetchedPosthogConfig;
  } catch (error) {
    return null;
  }
}

export function initPosthog() {
  if (!browserSupportsPosthog()) {
    return null;
  }

  const { apiKey, host } = getPosthogConfig();
  if (!apiKey) {
    return null;
  }

  installSnippet();
  if (!window.__seedlangPosthogInitialized) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[PostHog] Initializing web SDK');
    }
    window.posthog.init(apiKey, {
      api_host: host,
      autocapture: false,
      capture_performance: browserSupportsPosthogPerformanceCapture(),
      defaults: '2026-01-30',
      before_send: fillInitialCampaignProperties,
    });
    window.__seedlangPosthogInitialized = true;
  }

  return window.posthog;
}

// PostHog derives $initial_utm_* from the URL it sees when it starts, which on
// Memrev is usually after consent or a redirect has removed the campaign params.
// Filling them into PostHog's own $set_once keeps them first-touch.
function fillInitialCampaignProperties(event) {
  const setOnceProperties = event && event.$set_once;
  if (!setOnceProperties || !isPresent(firstTouchCampaignProperties)) {
    return event;
  }

  CAMPAIGN_PARAMS.forEach((param) => {
    const initialKey = `$initial_${param}`;
    const value = firstTouchCampaignProperties[`first_touch_${param}`];
    if (isPresent(value) && !isPresent(setOnceProperties[initialKey])) {
      setOnceProperties[initialKey] = value;
    }
  });

  return event;
}

function restoreFirstTouchCampaignProperties(posthog) {
  if (isPresent(firstTouchCampaignProperties)) {
    return;
  }

  const storedCampaignProperties = storedFirstTouchCampaignProperties();
  firstTouchCampaignProperties = isPresent(storedCampaignProperties) ? storedCampaignProperties : landingCampaignProperties;
  if (isPresent(firstTouchCampaignProperties)) {
    storeFirstTouchCampaignProperties(firstTouchCampaignProperties);
    posthog.register_once(firstTouchCampaignProperties);
  }
}

function storedFirstTouchCampaignProperties() {
  try {
    return JSON.parse(window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
}

function storeFirstTouchCampaignProperties(properties) {
  try {
    window.localStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(properties));
  } catch (error) {
    // Storage can be unavailable in private windows; events still carry the values for this page.
  }
}

function clearFirstTouchCampaignProperties() {
  firstTouchCampaignProperties = {};
  try {
    window.localStorage.removeItem(FIRST_TOUCH_STORAGE_KEY);
  } catch (error) {
    // Nothing was stored if storage is unavailable.
  }
}

function resetPosthog(posthog) {
  posthog.reset();
  clearFirstTouchCampaignProperties();
  delete window.__seedlangPosthogDistinctId;
  delete window.__seedlangPosthogIdentifyKey;
}

export function shouldTrackWebPosthog({ siteIsSeedlang, user, allowCookies }) {
  if (!browserSupportsPosthog()) {
    return false;
  }
  if (!user || (!user.signedOut && !user.id)) {
    return false;
  }
  if (user && user.blockUserTracking) {
    return false;
  }
  if (siteIsSeedlang) {
    return true;
  }

  const privacySettings = user.privacySettings;
  if (privacySettings && typeof privacySettings.allowCookies !== 'undefined') {
    return Boolean(privacySettings.allowCookies);
  }

  return Boolean(allowCookies);
}

export function setPosthogTrackingEnabled(enabled) {
  const posthog = enabled ? initPosthog() : initializedPosthog();
  if (!posthog) {
    return false;
  }

  if (enabled) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
  }

  return true;
}

export function syncPosthogUser(user, { trackingAllowed, site } = {}) {
  if (!trackingAllowed) {
    clearFirstTouchCampaignProperties();
    const posthog = initializedPosthog();
    if (!posthog) {
      return false;
    }

    setPosthogTrackingEnabled(false);
    if (window.__seedlangPosthogDistinctId) {
      resetPosthog(posthog);
    }
    return false;
  }

  const posthog = initPosthog();
  if (!posthog) {
    return false;
  }

  setPosthogTrackingEnabled(true);
  restoreFirstTouchCampaignProperties(posthog);

  if (user && user.id && !user.signedOut) {
    const distinctId = String(user.id);
    const personProperties = posthogPersonProperties(user, site || user.site);
    const identifyKey = JSON.stringify([distinctId, personProperties]);
    if (window.__seedlangPosthogIdentifyKey !== identifyKey) {
      posthog.identify(distinctId, personProperties, firstTouchCampaignProperties);
      window.__seedlangPosthogDistinctId = distinctId;
      window.__seedlangPosthogIdentifyKey = identifyKey;
    }
    return true;
  }

  if (window.__seedlangPosthogDistinctId) {
    resetPosthog(posthog);
  }

  return true;
}

export function capturePosthogEvent(eventType, properties = {}) {
  const posthog = initializedPosthog();
  if (!posthog) {
    return false;
  }

  if (isDebugLoggingHost()) {
    console.log('[PostHog frontend event]', {
      eventType,
      properties,
    });
  }

  posthog.capture(eventType, properties);
  return true;
}

export function getPosthogSessionId() {
  const posthog = initializedPosthog();
  if (!posthog || typeof posthog.get_session_id !== 'function') {
    return null;
  }

  return posthog.get_session_id();
}
